import { useT, Trans } from '@kmgestao/i18n';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  toast,
} from '@kmgestao/ui';

import {
  type ProvisionPlan,
  useProvisionClinic,
  type ProvisionClinicResult,
} from '../../../features/leads/index.js';

export interface ProvisionClinicDialogProps {
  /** ID do lead — pra invalidar caches após sucesso. */
  leadId: string;
  /** ID do contrato assinado que ancora o provisionamento. */
  contractId: string;
  /** Nome do lead/clínica pra exibir no diálogo. */
  clinicNameHint: string;
  /** Email pra mostrar onde o welcome será enviado. */
  ownerEmail: string;
  /** Plano de provisionamento computado (preço efetivo, trial efetivo,
   * status inicial, gate mode, parcelamento da consultoria). Single
   * source of truth — mesmo helper que o edge function usa. */
  provisionPlan: ProvisionPlan;
  /** 'monthly' ou 'yearly' — afeta texto do banner. */
  billingCycle: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onProvisioned?: (result: ProvisionClinicResult) => void;
}

/**
 * Dialog de confirmação antes de provisionar a clínica. Comunica
 * o **modelo de cobrança real** (Modelo C híbrido — ADR 0009 +
 * extensão PR-M1-01h.b pra combo SaaS+Consultoria com desconto):
 *
 *   - **trialing**: acesso imediato (trial explícito ou implícito via
 *     saas_free_during_consulting)
 *   - **incomplete**: gate de pagamento ativo (espera 1ª fatura SaaS)
 *   - **active**: SaaS R$ 0 sem trial (consulting puro / 100% desc) —
 *     sem gate, libera direto
 *
 * Banner colorido + bullets adaptam ao plano. Quando há consultoria
 * com parcelamento, mostra estrutura completa (SaaS + N parcelas).
 */
export function ProvisionClinicDialog({
  leadId,
  contractId,
  clinicNameHint,
  ownerEmail,
  provisionPlan,
  billingCycle,
  open,
  onOpenChange,
  onProvisioned,
}: ProvisionClinicDialogProps) {
  const { t } = useT('clinics');
  const provision = useProvisionClinic();

  const cycleLabel =
    billingCycle === 'yearly'
      ? t('provisionDialog.cycleYearly')
      : t('provisionDialog.cycleMonthly');
  const saasPriceLabel = formatBrl(provisionPlan.saasMonthlyPrice);
  const consultingTotalLabel = formatBrl(provisionPlan.consultingTotal);
  const consultingInstallmentLabel = formatBrl(provisionPlan.consultingInstallmentValue);

  const onConfirm = async () => {
    await provision.mutateAsync(
      { lead_id: leadId, contract_id: contractId },
      {
        onSuccess: (result) => {
          if (result.mode === 'cached') {
            toast.info(t('provisionDialog.toast.cached.title'), {
              description: t('provisionDialog.toast.cached.description', {
                id: result.clinic_id ?? '—',
              }),
            });
          } else if (result.status === 'failed') {
            toast.error(t('provisionDialog.toast.failed.title'), {
              description: t('provisionDialog.toast.failed.description', {
                step: result.step ?? '—',
                reason: result.reason ?? '—',
              }),
            });
          } else {
            toast.success(t('provisionDialog.toast.success.title'), {
              description: t('provisionDialog.toast.success.description'),
            });
          }
          onOpenChange(false);
          onProvisioned?.(result);
        },
        onError: (err) =>
          toast.error(t('provisionDialog.toast.error'), { description: (err as Error).message }),
      },
    );
  };

  // Decide cor do banner conforme status inicial.
  const bannerTone =
    provisionPlan.initialSubscriptionStatus === 'incomplete' ? 'warning' : 'accent';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('provisionDialog.title')}</DialogTitle>
          <DialogDescription>
            <Trans
              i18nKey="provisionDialog.description"
              ns="clinics"
              values={{ name: clinicNameHint }}
              components={{ strong: <strong className="text-foreground" /> }}
            />
          </DialogDescription>
        </DialogHeader>

        {/* Banner do modelo de cobrança — adapta texto e cor conforme
            status inicial decidido pelo helper. */}
        <div
          className={`rounded-md border p-3 text-sm ${
            bannerTone === 'warning'
              ? 'border-warning/40 bg-warning/5 text-foreground'
              : 'border-accent/40 bg-accent/5 text-foreground'
          }`}
          role="status"
        >
          <ProvisionBannerContent
            plan={provisionPlan}
            saasPriceLabel={saasPriceLabel}
            cycleLabel={cycleLabel}
            consultingTotalLabel={consultingTotalLabel}
            consultingInstallmentLabel={consultingInstallmentLabel}
          />
        </div>

        <div className="space-y-3 rounded-md border border-border bg-muted/40 p-4 text-sm">
          <p className="font-medium text-foreground">{t('provisionDialog.checklist.title')}</p>
          <ul className="space-y-1.5 text-muted-foreground">
            <li className="flex gap-2">
              <span className="text-accent-foreground">→</span>
              <span>
                <Trans
                  i18nKey="provisionDialog.checklist.createsRecords"
                  ns="clinics"
                  components={{
                    code: <code className="font-mono text-xs" />,
                  }}
                />
              </span>
            </li>
            <li className="flex gap-2">
              <span className="text-accent-foreground">→</span>
              <span>
                <ProvisionChecklistAsaasBullet plan={provisionPlan} />
              </span>
            </li>
            <li className="flex gap-2">
              <span className="text-accent-foreground">→</span>
              <span>
                {/* Acesso liberado direto (trialing/active): email vai
                    agora. Gate ativo (incomplete): email só após
                    PAYMENT_CONFIRMED. */}
                {provisionPlan.initialSubscriptionStatus === 'incomplete' ? (
                  <Trans
                    i18nKey="provisionDialog.checklist.emailAfterPayment"
                    ns="clinics"
                    values={{ email: ownerEmail }}
                    components={{
                      strong: <strong className="break-all text-foreground" />,
                    }}
                  />
                ) : (
                  <Trans
                    i18nKey="provisionDialog.checklist.emailNow"
                    ns="clinics"
                    values={{ email: ownerEmail }}
                    components={{
                      strong: <strong className="break-all text-foreground" />,
                    }}
                  />
                )}
              </span>
            </li>
            <li className="flex gap-2">
              <span className="text-accent-foreground">→</span>
              <span>
                <Trans
                  i18nKey="provisionDialog.checklist.transition"
                  ns="clinics"
                  components={{
                    code: <code className="font-mono text-xs" />,
                  }}
                />
              </span>
            </li>
          </ul>
        </div>

        <DialogFooter className="gap-2">
          <DialogClose asChild>
            <Button type="button" variant="outline" size="lg" disabled={provision.isPending}>
              {t('provisionDialog.actions.cancel')}
            </Button>
          </DialogClose>
          <Button type="button" size="lg" onClick={onConfirm} disabled={provision.isPending}>
            {provision.isPending
              ? t('provisionDialog.actions.submitting')
              : t('provisionDialog.actions.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Conteúdo do banner do modelo de cobrança — extraído pra simplificar
 * o JSX do componente principal. Cobre 5 cenários distintos. */
function ProvisionBannerContent({
  plan,
  saasPriceLabel,
  cycleLabel,
  consultingTotalLabel,
  consultingInstallmentLabel,
}: {
  plan: ProvisionPlan;
  saasPriceLabel: string;
  cycleLabel: string;
  consultingTotalLabel: string;
  consultingInstallmentLabel: string;
}) {
  const { t } = useT('clinics');

  // Cenário 1: combo + saas_free (saasPrice=0 + trialing pelos meses
  // de consultoria) → "SaaS gratuito durante consultoria"
  if (plan.type === 'saas_consulting' && plan.saasMonthlyPrice === 0 && plan.willChargeConsulting) {
    return (
      <>
        <p className="font-semibold">{t('provisionDialog.banner.comboSaasFree.title')}</p>
        <p className="mt-1 text-muted-foreground">
          <Trans
            i18nKey="provisionDialog.banner.comboSaasFree.description"
            ns="clinics"
            values={{
              count: plan.consultingInstallmentCount,
              installment: consultingInstallmentLabel,
              total: consultingTotalLabel,
            }}
            components={{ strong: <strong className="text-foreground" /> }}
          />
        </p>
      </>
    );
  }

  // Cenário 2: combo + saasPrice > 0 + status incomplete (gate)
  if (
    plan.type === 'saas_consulting' &&
    plan.saasMonthlyPrice > 0 &&
    plan.initialSubscriptionStatus === 'incomplete'
  ) {
    return (
      <>
        <p className="font-semibold">{t('provisionDialog.banner.comboGate.title')}</p>
        <p className="mt-1 text-muted-foreground">
          <Trans
            i18nKey="provisionDialog.banner.comboGate.description"
            ns="clinics"
            values={{
              price: saasPriceLabel,
              cycle: cycleLabel,
              count: plan.consultingInstallmentCount,
              installment: consultingInstallmentLabel,
            }}
            components={{ strong: <strong className="text-foreground" /> }}
          />
        </p>
      </>
    );
  }

  // Cenário 3: consulting puro
  if (plan.type === 'consulting') {
    return (
      <>
        <p className="font-semibold">{t('provisionDialog.banner.consultingOnly.title')}</p>
        <p className="mt-1 text-muted-foreground">
          {t('provisionDialog.banner.consultingOnly.description', {
            count: plan.consultingInstallmentCount,
            installment: consultingInstallmentLabel,
            total: consultingTotalLabel,
          })}
        </p>
      </>
    );
  }

  // Cenário 4: SaaS puro com trial
  if (plan.type === 'saas' && plan.effectiveTrialDays > 0) {
    return (
      <>
        <p className="font-semibold">
          {t('provisionDialog.banner.saasTrial.title', { days: plan.effectiveTrialDays })}
        </p>
        <p className="mt-1 text-muted-foreground">
          {t('provisionDialog.banner.saasTrial.description', {
            days: plan.effectiveTrialDays,
            price: saasPriceLabel,
            cycle: cycleLabel,
          })}
        </p>
      </>
    );
  }

  // Cenário 5: SaaS puro sem trial (gate ativo) — fallback default
  return (
    <>
      <p className="font-semibold">{t('provisionDialog.banner.saasGate.title')}</p>
      <p className="mt-1 text-muted-foreground">
        <Trans
          i18nKey="provisionDialog.banner.saasGate.description"
          ns="clinics"
          values={{ price: saasPriceLabel, cycle: cycleLabel }}
          components={{ strong: <strong className="text-foreground" /> }}
        />
      </p>
    </>
  );
}

/** Bullet do checklist sobre Asaas — adapta conforme estrutura
 * (subscription, parcelamento, ambos, ou nenhum). */
function ProvisionChecklistAsaasBullet({ plan }: { plan: ProvisionPlan }) {
  const { t } = useT('clinics');
  const hasSaasCharge = plan.saasMonthlyPrice > 0;
  const hasConsulting = plan.willChargeConsulting;

  if (hasSaasCharge && hasConsulting) {
    return (
      <Trans
        i18nKey="provisionDialog.checklist.asaasBoth"
        ns="clinics"
        components={{ strong: <strong className="text-foreground" /> }}
      />
    );
  }
  if (hasConsulting) {
    return (
      <Trans
        i18nKey="provisionDialog.checklist.asaasInstallmentOnly"
        ns="clinics"
        components={{ strong: <strong className="text-foreground" /> }}
      />
    );
  }
  if (hasSaasCharge) {
    return (
      <Trans
        i18nKey="provisionDialog.checklist.asaasSaasOnly"
        ns="clinics"
        components={{ strong: <strong className="text-foreground" /> }}
      />
    );
  }
  // Nenhum dos dois — caso de borda (consulting puro com installments=0
  // ou SaaS puro com 100% desc + sem trial).
  return <span>{t('provisionDialog.checklist.asaasNone')}</span>;
}

function formatBrl(value: number): string {
  try {
    return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  } catch {
    return `R$ ${value}`;
  }
}
